<template>
  <v-sheet
    outlined
    class="pa-4 h-100 d-flex flex-column"
  >
    <p class="text-subtitle-1 font-weight-bold">
      {{ priceInfo.name }}
    </p>
    <v-divider />
    <div class="py-4 text-center">
      <!-- Original Price -->
      <div
        v-if="priceInfo.originalMonthlyAmount"
        class="text--secondary"
      >
        <s>
          <span class="font-weight-medium text-h5">
            {{ integerPart(priceInfo.originalMonthlyAmount) }}
          </span>
          <span class="text-h5">.</span>
          <sup class="font-weight-medium text-body-2">{{ fractionPart(priceInfo.originalMonthlyAmount) }}</sup>
        </s>
        /{{ $t('label.month') }}
      </div>
      <!-- Current Price -->
      <span class="font-weight-bold text-h5">
        {{ integerPart(priceInfo.monthlyAmount) }}
      </span>
      <span class="text-h5">.</span>
      <sup class="font-weight-bold text-body-2">{{ fractionPart(priceInfo.monthlyAmount) }}</sup>
      /{{ $t('label.month') }}
    </div>
    <div
      v-for="(f, j) in features"
      :key="`feature_${j}`"
      :class="['text--primary text-body-2 pb-2', {'font-weight-bold': j === 0 && !isLowest}]"
    >
      <v-icon
        x-small
        color="success"
        class="mr-2"
      >
        mdi-check
      </v-icon>{{ f }}
    </div>
    <div class="flex-grow-1 pt-2" />
    <div v-if="showDiscount && priceInfo.discountInfo">
      <template v-if="priceInfo.discountInfo.offerExpiresDescription">
        {{ priceInfo.discountInfo.offerExpiresDescription }}
      </template>
      <template v-if="priceInfo.discountInfo.validUntilOrDurationDescription">
        {{ priceInfo.discountInfo.validUntilOrDurationDescription }}
      </template>
    </div>
    <v-divider class="py-2" />
    <v-btn
      v-if="!unavailablePlan"
      :disabled="disabled"
      color="green"
      rounded
      class="white--text"
      elevation="0"
      @click="emit('plan-selected')"
    >
      {{ currentPlan ? $t('label.your_current_plan') : $t('button.select_plan') }}
    </v-btn>
    <div
      v-else
      style="height: 36px;"
    />
  </v-sheet>
</template>

<script setup lang="ts">
import { Price } from '@/api/api-billing';
import { PropType, computed } from 'vue';
import { subscriptionPriceInfo, SubscriptionPriceInfo } from '@/services/billing-utils';
import { TranslateResult } from 'vue-i18n';

const emit = defineEmits(['plan-selected']);
const props = defineProps({
  isLowest: { type: Boolean, required: false, default: false },
  features: { type: Array as PropType<Array<string|TranslateResult>>, required: true },
  price: { type: Object as PropType<Price>, required: true },
  showDiscount: { type: Boolean, required: false, default: false },
  disabled: { type: Boolean, required: false, default: false },
  currentPlan: { type: Boolean, required: false, default: false },
  unavailablePlan: { type: Boolean, required: false, default: false },
});

const priceInfo = computed<SubscriptionPriceInfo>(() => subscriptionPriceInfo(props.price));

const integerPart = (amt: string) => amt.split('.')[0];
const fractionPart = (amt: string) => amt.split('.')[1];

</script>

<style lang="scss" scoped>
  s, strike{text-decoration:none;position:relative;}

  s::before, strike::before {
    top: 30%;
    background:$red-color;
    opacity:.7;
    content: '';
    width: 110%;
    position: absolute;
    height:.2em;
    border-radius:.2em;
    left: -5%;
    white-space:nowrap;
    display: block;
    transform: rotate(-15deg);
  }

  s.straight::before, strike.straight::before{transform: rotate(0deg);left:-1%;width:102%;}
</style>
